import React, {createContext, useContext, useMemo} from 'react';
import {useParams} from 'react-router-dom';

import {useWorkspaceSingleRunQuery} from '../../../generated/graphql';
import {
  useIsSingleRunDeprecated,
  useRunWorkspaceRunContextMaybe,
} from '../../../pages/Run/RunWorkspaceRunContext';
import {useStatsigGateWorkspaceConsolidation} from '../../../util/rampFeatureFlags';
import {mapDeprecatedRun, mapQueryDataToRun, SingleRunModeRun} from './utils';

type SingleRunModeProviderProps = {
  children: React.ReactNode;
};

type SingleRunModeContextValue = {
  /** whether we are in single-run mode; determined by presence of :runName param in URL */
  isSingleRun: boolean;
  /** whether the run query is loading */
  isLoading: boolean;
  /** populated if in single-run mode and the run loads successfully */
  run: SingleRunModeRun | undefined;
};

// This context has consumers that appear in both workspaces and reports.
// Workspaces will be wrapped in the provider, but reports will not (since
// there's no single-run mode for reports). A valid default value is set here
// so the context can be safely used in reports too.
const SingleRunModeContext = createContext<SingleRunModeContextValue>({
  isSingleRun: false,
  isLoading: false,
  run: undefined,
});

export const SingleRunModeContextProvider = ({
  children,
}: SingleRunModeProviderProps) => {
  const isWorkspaceConsolidationEnabled =
    useStatsigGateWorkspaceConsolidation();

  const {entityName, projectName, runName} = useParams<{
    entityName: string;
    projectName: string;
    runName?: string;
  }>();

  const isSingleRun = runName != null;
  const runQuery = useWorkspaceSingleRunQuery({
    variables: {
      entityName,
      projectName,
      runName: runName ?? '',
    },
    skip: !isWorkspaceConsolidationEnabled || !isSingleRun,
  });

  const value = useMemo<SingleRunModeContextValue>(() => {
    return {
      isSingleRun,
      isLoading: runQuery.loading,
      run: mapQueryDataToRun(runQuery.data),
    };
  }, [isSingleRun, runQuery]);

  return (
    <SingleRunModeContext.Provider value={value}>
      {children}
    </SingleRunModeContext.Provider>
  );
};

export const useSingleRunModeContext = (): SingleRunModeContextValue => {
  // Skip usual "must be inside provider" check because this context
  // has a default value and is safe to call outside of the provider.
  const value = useContext(SingleRunModeContext);

  // Account for feature flag -- read from old context if feature isn't enabled yet
  const isWorkspaceConsolidationEnabled =
    useStatsigGateWorkspaceConsolidation();
  const isSingleRunDeprecated = useIsSingleRunDeprecated();
  const runDeprecated = useRunWorkspaceRunContextMaybe();
  const valueDeprecated = useMemo<SingleRunModeContextValue>(
    () => ({
      isSingleRun: isSingleRunDeprecated,
      isLoading: false,
      run: mapDeprecatedRun(runDeprecated),
    }),
    [isSingleRunDeprecated, runDeprecated]
  );

  return isWorkspaceConsolidationEnabled ? value : valueDeprecated;
};
